import React, { useState, useEffect } from "react";

import { FieldArray, Formik, Form } from "formik";
import { Plus } from "icons";

import providerManagerEntitiesApi from "apis/providerManagerEntity";
import ProviderManagerTemplatesApi from "apis/providerManagerTemplate";
import { Button, PageLoader, Pane, Typography } from "clearsense-ui/components";
import {
  Input,
  Select,
} from "components/Common/clearsense-ui/components/formik";

import Hierarchy from "./Hierarchy";

import { CREATE_TEMPLATE_INITIAL_VALUES } from "../constants";
import { ADD_TEMPLATE_VALIDATION } from "../validations";

const CreateTemplate = ({
  isCreateTemplate,
  setIsCreateTemplate,
  templateId,
  setEditRecordId,
  getProviderManagerTemplates,
}) => {
  const [isTemplateLoading, setIsTemplateLoading] = useState(false);
  const [templateDetails, setTemplateDetails] = useState();
  const [entities, setEntities] = useState([]);

  useEffect(() => {
    isCreateTemplate && fetchTemplateEntities();
  }, [isCreateTemplate]);

  const fetchTemplateEntities = async () => {
    try {
      const { data } =
        await providerManagerEntitiesApi.fetchTemplatelessEntities();
      setEntities(data.entities);
    } catch (err) {
      logger.log(err);
    }
  };

  useEffect(() => {
    const getTemplateDetails = async id => {
      try {
        setIsTemplateLoading(true);
        const { data } = await ProviderManagerTemplatesApi.show(id);
        const transformTemplateToFormikFormat = {
          ...data,
          hierarchies: data.template.hierarchies.map(hierarchy => ({
            name: hierarchy.name,
            isEdit: false,
            isInitial: false,
            fields: hierarchy.fields.map(field => ({
              ...field,
              type: { label: field.type, value: field.type },
              isEdit: false,
              isInitial: false,
            })),
          })),
          entity: { label: data.entity?.name, value: data.entity?.id },
        };
        setTemplateDetails(transformTemplateToFormikFormat);
      } catch (error) {
        logger.error(error);
      } finally {
        setIsTemplateLoading(false);
      }
    };

    templateId && getTemplateDetails(templateId);
  }, [templateId]);

  const handleSave = async data => {
    const payload = {
      name: data.name,
      provider_manager_entity_id: data.entity.value,
      template: {
        hierarchies: data.hierarchies.map(hierarchy => ({
          name: hierarchy.name,
          fields: hierarchy.fields.map(field => ({
            name: field.name,
            type: field.type.value,
          })),
        })),
      },
    };
    try {
      templateId
        ? await ProviderManagerTemplatesApi.update(payload, templateId)
        : await ProviderManagerTemplatesApi.create(payload);
      setEditRecordId();
      getProviderManagerTemplates();
    } catch (error) {
      logger.error(error);
    } finally {
      setIsCreateTemplate(false);
    }
  };
  return (
    <Formik
      initialValues={
        templateDetails ? templateDetails : CREATE_TEMPLATE_INITIAL_VALUES
      }
      validationSchema={ADD_TEMPLATE_VALIDATION}
      onSubmit={data => handleSave(data)}
    >
      {({ values, submitForm, resetForm }) => (
        <Form className="w-full" noValidate>
          <Pane
            isOpen={isCreateTemplate}
            onClose={() => {
              setEditRecordId();
              setTemplateDetails();
              setIsCreateTemplate(false);
            }}
          >
            <Pane.Header>
              <Typography style="h3" type="semibold" className="-mt-3">
                {templateId ? "Edit" : "Create"} a Template
              </Typography>
              <Typography style="large">
                {templateId ? "Edit" : "Create"} a Template with Hierarchy's and
                its fields
              </Typography>
            </Pane.Header>
            {isTemplateLoading ? (
              <PageLoader />
            ) : (
              <>
                <Pane.Body>
                  <Input
                    name="name"
                    label="Name"
                    placeholder="Enter Template name"
                    className="flex-grow-0 w-full mt-6"
                    required
                  />
                  <Select
                    name="entity"
                    label="Entity"
                    placeholder="Select the entity"
                    className="flex-grow-0 w-full mt-6"
                    isDisabled={!!templateId}
                    options={entities.map(entity => ({
                      label: entity.name,
                      value: entity.id,
                    }))}
                    required
                  />
                  <Typography style="h5" type="bold" className="mt-5">
                    Hierarchies
                  </Typography>
                  <div className="w-full">
                    <FieldArray name="hierarchies">
                      {arrayHelpers => (
                        <>
                          {values.hierarchies.map((hierarchy, index) => (
                            <Hierarchy
                              key={index}
                              index={index}
                              hierarchy={hierarchy}
                              arrayHelpers={arrayHelpers}
                            />
                          ))}
                          <Button
                            label="Create Hierarchy"
                            icon={Plus}
                            style="secondary"
                            className="mt-4"
                            disabled={values.hierarchies
                              .map(field => field?.isEdit)
                              .includes(true)}
                            onClick={() => {
                              arrayHelpers.push({
                                name: "",
                                fields: [
                                  {
                                    name: "",
                                    type: undefined,
                                    isEdit: true,
                                    isInitial: true,
                                  },
                                ],
                                isEdit: true,
                                isInitial: true,
                              });
                            }}
                          />
                        </>
                      )}
                    </FieldArray>
                  </div>
                </Pane.Body>
                <Pane.Footer className="space-x-4">
                  <Button
                    label="Save"
                    size="medium"
                    type="submit"
                    data-cy="submit-provider-template-button"
                    onClick={submitForm}
                  />
                  <Button
                    label="Cancel"
                    style="secondary"
                    size="medium"
                    onClick={() => {
                      setIsCreateTemplate(false);
                      resetForm();
                    }}
                  />
                </Pane.Footer>
              </>
            )}
          </Pane>
        </Form>
      )}
    </Formik>
  );
};

export default CreateTemplate;
